import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isNative } from '../lib/platform';
import { apiGet, isTransientError, retryDelayMs } from '../lib/api';
import { featureEnabled, type FeatureState } from '../lib/features';
import { SsoRequiredError } from '../lib/auth';
import { safeReturnPath, type LoginRedirectState } from '../lib/loginRedirect';

/**
 * Messages de retour du parcours SSO (paramètre ?sso=... posé par l'API sur
 * /api/auth/oidc/callback). Volontairement peu bavards : ils ne doivent pas
 * révéler si une adresse existe côté Jarvis.
 */
const SSO_ERRORS: Record<string, string> = {
  inconnu:
    "Ce compte DigitalTeam n'est associé à aucun accès Jarvis. Connectez-vous avec vos identifiants Jarvis, ou demandez la création de l'accès.",
  mfa: 'Votre compte exige une double authentification. Reconnectez-vous en validant le second facteur.',
  conflit: 'Ce compte est déjà associé à une autre identité DigitalTeam. Contactez le support.',
  expire: 'La connexion a expiré. Merci de recommencer.',
  indisponible: "Le service d'authentification DigitalTeam est momentanément injoignable.",
  echec: 'La connexion DigitalTeam a échoué. Merci de réessayer.',
};

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation() as { state?: Partial<LoginRedirectState> | null };
  const [mail, setMail] = useState('');
  const [pwd, setPwd] = useState('');
  const [mfa, setMfa] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  // Interrupteurs publics : le lien « mot de passe oublié » mène à une écriture
  // du mot de passe partagé avec Jarvis V1, fermée pendant la transition.
  const [publicFeatures, setPublicFeatures] = useState<FeatureState | null>(null);
  useEffect(() => {
    let annule = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const charger = () => {
      apiGet<FeatureState>('/api/features')
        .then((f) => {
          if (!annule) setPublicFeatures(f);
        })
        .catch((e: unknown) => {
          if (annule) return;
          setPublicFeatures(null);
          // 429 / 5xx / réseau : nouvel essai après Retry-After (borné), sans bruit.
          if (isTransientError(e)) timer = setTimeout(charger, retryDelayMs(e));
        });
    };
    charger();
    return () => {
      annule = true;
      if (timer) clearTimeout(timer);
    };
  }, []);
  // required_dt : connexion DigitalTeam obligatoire pour les agents Digital
  // Team (FEATURE_SSO_REQUIRED_DT) → le bouton passe en tête de la page.
  const [sso, setSso] = useState<{ enabled: boolean; label: string; required_dt?: boolean } | null>(null);
  const [ssoRequired, setSsoRequired] = useState<string | null>(null);
  const [params] = useSearchParams();

  // Le bouton n'apparaît que si l'API annonce le SSO configuré. Sur mobile
  // (Capacitor) on ne le propose pas : le parcours par redirection de page ne
  // convient pas à une WebView embarquée — ce sera un chantier à part.
  useEffect(() => {
    if (isNative()) return;
    let annule = false;
    fetch('/api/auth/oidc/enabled', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!annule && d && typeof d.enabled === 'boolean') setSso(d);
      })
      .catch(() => {
        /* SSO simplement non proposé */
      });
    return () => {
      annule = true;
    };
  }, []);

  const ssoErr = params.get('sso');
  const ssoFirst = !!sso?.enabled && !!sso.required_dt;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setSsoRequired(null);
    try {
      await login(mail.trim(), pwd, mfa || undefined);
      navigate(safeReturnPath(loc.state?.from), { replace: true });
    } catch (e2) {
      if (e2 instanceof SsoRequiredError) {
        setSsoRequired(e2.ssoUrl);
        setMfaRequired(false);
        setMfa('');
        return;
      }
      const msg = e2 instanceof Error ? e2.message : 'Erreur de connexion';
      // Backend renvoie 428 "Code MFA requis" la 1ère fois quand MFA est activé
      if (msg.includes('Code MFA requis')) {
        setMfaRequired(true);
        setErr(null);
      } else {
        setErr(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '16px',
        background: 'var(--bg-soft)',
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          width: '100%',
          maxWidth: 360,
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          padding: '28px 28px 24px',
          boxShadow: 'var(--shadow)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <img src="/dt-logo.png" alt="Digital Team" className="brand-logo" style={{ height: 64 }} />
        </div>
        <div
          style={{
            fontSize: 'var(--fs-md)',
            fontWeight: 600,
            color: 'var(--text)',
            textAlign: 'center',
            marginBottom: 4,
            letterSpacing: '-0.01em',
          }}
        >
          Jarvis Support
        </div>
        {ssoFirst ? (
          <>
            <a href="/api/auth/oidc/start" className="btn primary" style={ssoButtonStyle}>
              Connexion {sso!.label}
            </a>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', textAlign: 'center', marginTop: 6 }}>
              Obligatoire pour les agents {sso!.label}.
            </div>
            <Divider label="clients : identifiants Jarvis" />
          </>
        ) : (
          <div style={{ textAlign: 'center', color: 'var(--text-mute)', fontSize: 'var(--fs-xs)', marginBottom: 22 }}>
            Connectez-vous avec vos identifiants Jarvis
          </div>
        )}

        <label style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginBottom: 4 }}>E-mail</label>
        <input
          type="email"
          value={mail}
          onChange={(e) => setMail(e.target.value)}
          autoFocus
          required
          autoComplete="username"
          style={inputStyle}
        />

        <label style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', margin: '12px 0 4px' }}>
          Mot de passe
        </label>
        <input
          type="password"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          required
          autoComplete="current-password"
          style={inputStyle}
        />

        {mfaRequired && (
          <>
            <label style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--accent-strong)', margin: '12px 0 4px', fontWeight: 500 }}>
              Code MFA (à 6 chiffres) ou code de récupération
            </label>
            <input
              autoFocus
              type="text"
              value={mfa}
              onChange={(e) => setMfa(e.target.value.slice(0, 32))}
              placeholder="123456"
              inputMode="numeric"
              autoComplete="one-time-code"
              style={inputStyle}
            />
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginTop: 4 }}>
              Ouvrez votre app TOTP (Google Authenticator, Authy, 1Password) et saisissez le code à 6 chiffres.
            </div>
          </>
        )}

        {!err && !ssoErr && loc.state?.expired && (
          <div
            role="status"
            style={{
              marginTop: 12,
              padding: '8px 12px',
              background: 'var(--warning-soft)',
              border: '1px solid var(--warning-border)',
              borderRadius: 6,
              color: 'var(--warning-text)',
              fontSize: 'var(--fs-sm)',
            }}
          >
            Votre session a expiré : reconnectez-vous pour reprendre où vous en étiez.
          </div>
        )}

        {!err && ssoErr && (
          <div
            style={{
              marginTop: 12,
              padding: '8px 12px',
              background: 'var(--warning-soft)',
              border: '1px solid var(--warning-border)',
              borderRadius: 6,
              color: 'var(--warning-text)',
              fontSize: 'var(--fs-sm)',
            }}
          >
            {SSO_ERRORS[ssoErr] ?? SSO_ERRORS.echec}
          </div>
        )}

        {ssoRequired && (
          <div
            role="alert"
            style={{
              marginTop: 12,
              padding: '8px 12px',
              background: 'var(--warning-soft)',
              border: '1px solid var(--warning-border)',
              borderRadius: 6,
              color: 'var(--warning-text)',
              fontSize: 'var(--fs-sm)',
            }}
          >
            <strong>Connexion DigitalTeam obligatoire.</strong>{' '}
            {isNative() ? (
              'Connectez-vous depuis un navigateur, par le bouton Connexion DigitalTeam.'
            ) : (
              <>
                Votre compte se connecte par le SSO DigitalTeam, pas par un mot de passe Jarvis.{' '}
                <a href={ssoRequired} style={{ color: 'inherit', fontWeight: 600 }}>
                  Se connecter avec DigitalTeam
                </a>
              </>
            )}
          </div>
        )}

        {err && (
          <div
            style={{
              marginTop: 12,
              padding: '8px 12px',
              background: 'var(--danger-soft)',
              border: '1px solid var(--danger-border)',
              borderRadius: 6,
              color: 'var(--danger)',
              fontSize: 'var(--fs-sm)',
            }}
          >
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className={ssoFirst ? 'btn' : 'btn primary'}
          style={{ width: '100%', marginTop: 16, padding: '10px' }}
        >
          {busy ? 'Connexion…' : 'Se connecter'}
        </button>

        {sso?.enabled && !ssoFirst && (
          <>
            <Divider label="ou" />
            <a href="/api/auth/oidc/start" className="btn" style={ssoButtonStyle}>
              Connexion {sso.label}
            </a>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', textAlign: 'center', marginTop: 6 }}>
              Réservé aux comptes {sso.label}.
            </div>
          </>
        )}

        {featureEnabled(publicFeatures, 'password_change') && (
          <div style={{ textAlign: 'center', marginTop: 14 }}>
            <Link to="/forgot" style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>
              Mot de passe oublié ?
            </Link>
          </div>
        )}

        {/* Documentation publique : sous les boutons, visible sans défilement. */}
        <div style={{ textAlign: 'center', marginTop: 14 }}>
          <Link to="/aide/connexion" style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}>
            Aide à la connexion
          </Link>
        </div>
      </form>
    </div>
  );
}

function Divider({ label }: { label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        margin: '18px 0 14px',
        color: 'var(--text-mute)',
        fontSize: 'var(--fs-xs)',
      }}
    >
      <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      {label}
      <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  );
}

const ssoButtonStyle = {
  display: 'block',
  width: '100%',
  padding: '10px',
  textAlign: 'center',
  textDecoration: 'none',
  boxSizing: 'border-box',
} as const;

const inputStyle = {
  width: '100%',
  padding: '9px 11px',
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  fontSize: 'var(--fs-sm)',
} as const;
